import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Col, Form, Row, Spin } from 'antd'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormCheckbox,
  FormSelect,
  FormTextareaInput,
} from '../../../common/components/sections'
import {
  StyledPageHeader,
  StyledPageTitle,
} from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import {
  createSelectOptions,
  showErrorNotification,
} from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { generateArrayOfNumbers } from '../../../common/utils'

import { useUpdateAllergenSimpleDetailsMutation } from '../../hooks'
import { allergenPropType } from '../../prop-types'
import { setAllergenPageIsDirty } from '../../redux/actions'

import { allergenSimpleDetailsFormInitialValues } from './initial-values'

const AllergenSimpleDetailsForm = ({ allergen, maxPosition }) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.ALLERGEN.UPDATE_SIMPLE_DETAILS,
  ])

  const [
    updateAllergenSimpleDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateAllergenSimpleDetailsMutation()

  const positionOptions = useMemo(
    () => createSelectOptions(generateArrayOfNumbers(maxPosition)),
    [maxPosition],
  )

  const initialValues = useMemo(
    () => allergenSimpleDetailsFormInitialValues(allergen),
    [allergen],
  )

  const handleSaveData = useCallback(
    data => updateAllergenSimpleDetails(allergen.id, data),
    [allergen.id, updateAllergenSimpleDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Allergen details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [
    initialValues.synonyms,
    initialValues.isExcludedFromSearch,
    initialValues.position,
    initialValues.description,
    resetForm,
  ])

  useEffect(() => {
    dispatch(setAllergenPageIsDirty({ isSimpleDetailsDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="editAllergen"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <StyledPageHeader>
        <StyledPageTitle>{allergen.name}</StyledPageTitle>
      </StyledPageHeader>

      <Row gutter={[16]}>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="description"
            label="Description"
            labelAbove
            placeholder="Input description..."
            autoSize
          />
          <FormSelect
            disabled={!isFormEnabled}
            name="position"
            labelAbove
            label="Position under category"
            showSearch={false}
            allowClear={false}
            options={positionOptions}
            placeholder="Select position..."
          />
        </Col>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="synonyms"
            label="Synonyms (separated by new line)"
            labelAbove
            placeholder="Input synonyms..."
            autoSize
          />
        </Col>
        <Col span={12}>
          <FormCheckbox
            disabled={!isFormEnabled}
            label="Exclude from mobile app search"
            name="isExcludedFromSearch"
          />
        </Col>
      </Row>
      <FormButtons
        permissions={[PERMISSIONS.ALLERGEN.UPDATE_SIMPLE_DETAILS]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

AllergenSimpleDetailsForm.propTypes = {
  allergen: allergenPropType.isRequired,
  maxPosition: PropTypes.number.isRequired,
}

export default AllergenSimpleDetailsForm
